// One key of a list area (lane B10 S20a; plan D14; DS row 120 ConfigTable; CR-066 "Change -> Validate -> Version ->
// Activate -> Audit" · "Given/When/Then: historical records retain configuration version used."). The Editor every list
// area registers: the values of the version in effect in a ConfigTable (*Effective version* in mono, the *Current*
// pill), *Show previous versions* adding the earlier versions' values (*Previous version*), *Edit · View history* on each
// row and *New version* as the page action; a card per version waiting on its second Admin (Draft) or on its date
// (Scheduled), with Old → New and *Approve* / *Reject*; the slide-over Change step (ListEditor) handing its body to the
// Journey; and *View history*, the published versions with who, when and why.
//
// Reads: `config.list` (the versions, newest first) and `config.open` (a version's body, its diff against the version
// in effect). Writes: `config.approve`, `config.reject` here; `config.propose` in the Journey. A refusal is drawn as
// the server words it, with its code (the proposer's own *Approve* answers `SOD_CONFLICT`).

import { useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { configApprove, configList, configOpen, configReject, type ConfigApproveOutput, type ConfigOpenOutput, type ProposalRow } from "@/api/tools";
import type { AppError } from "@/api/runtime";
import { useRead, useWrite } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import {
  ACTION_LABELS, Button, COMMON_LABELS, Card, ConfigTable, DEFAULT_TIME_ZONE, Diff, Flex, G6_LABELS, HISTORY_LABELS, JOURNEY_LABELS,
  KeyValue, PANEL_CHANGE_STATUS, Pill, Recorded, SCHEDULING_STATUS, SURFACE_LABELS, SlideOver, Stack, TextArea,
  VERSION_TEMPLATES, Versions, formatDateTime, type ConfigRow, type VersionRow,
} from "@/mc";
import { CONSOLE_LABELS, keyWord } from "./labels";
import { Journey, JourneyStrip, Refusal, changeRows } from "./Journey";
import { ListEditor, cloneBody, emptyBody, entriesOf, listField, type Body } from "./ListEditor";
import { hsKey } from "@/views/b10/lib/hsKey";

// ---- reads --------------------------------------------------------------------------------------------------------------

/** Every version `config.list` holds for a key or a kind (newest first per key), following its cursor. */
export async function listVersions(filter: { key?: string | undefined; kind?: string | undefined } = {}): Promise<ProposalRow[]> {
  const rows: ProposalRow[] = [];
  let cursor: string | null = null;
  for (let page = 0; page < 25; page += 1) {
    const out = await configList({ key: filter.key ?? null, kind: filter.kind ?? null, limit: 200, cursor });
    rows.push(...out.rows);
    if (!out.next_cursor) break;
    cursor = out.next_cursor;
  }
  return rows;
}

/** The word of a version's state: the version in effect is *Current*, an earlier one *Previous version* (both pending
 * his confirmation, Q-E16), an approved one waiting on its date *Scheduled*, then *Draft* and *Rejected*. */
export const VERSION_STATUS_WORD: Readonly<Record<ProposalRow["status"], string>> = Object.freeze({
  effective: G6_LABELS.current,
  superseded: G6_LABELS.previousVersion,
  approved: SCHEDULING_STATUS.scheduled,
  draft: PANEL_CHANGE_STATUS.draft,
  rejected: PANEL_CHANGE_STATUS.rejected,
});

export interface VersionBody {
  version: number;
  status: ProposalRow["status"];
  body: Body | null | undefined;
}

/** The ConfigTable's rows: the values of the version in effect (*Current*), then each earlier version's (*Previous
 * version*, newest first). Drafts, scheduled and rejected versions are not values in effect and are not rows. */
export function versionRows(kind: string, versions: readonly VersionBody[]): ConfigRow[] {
  const field = listField(kind);
  const shown = versions
    .filter((v) => v.status === "effective" || v.status === "superseded")
    .sort((a, b) => Number(b.status === "effective") - Number(a.status === "effective") || b.version - a.version);
  return shown.flatMap((v) =>
    entriesOf(kind, v.body).map((entry) => ({
      key: entry.slug,
      name: entry.label ?? <span className="mc-mono">{entry.slug}</span>,
      value: field === "labels"
        ? <span className="mc-mono">{entry.slug}</span>
        : entry.active ? <Pill tone="ok">{CONSOLE_LABELS.active}</Pill> : null,
      version: VERSION_TEMPLATES.version({ n: v.version }),
      current: v.status === "effective",
      previous: v.status === "superseded",
      hs: v.status === "effective" ? hsKey("cfg.row", entry.slug) : undefined,
    })),
  );
}

// ---- the panel --------------------------------------------------------------------------------------------------------------

type Phase = { step: "change"; body: Body; focus?: string | undefined } | { step: "journey"; body: Body };

/** The Editor of a list area: one reason list, taxonomy or status-label set. */
export function ListArea({ kind, configKey }: { kind: string; configKey: string }) {
  const { me, can } = useSession();
  const timeZone = me?.timezone || DEFAULT_TIME_ZONE;
  const word = keyWord(configKey) ?? configKey;
  const versions = useRead(["config", "key", configKey], () => listVersions({ key: configKey }));
  const rows = versions.data ?? [];
  const current = rows.find((r) => r.status === "effective");
  const waiting = rows.filter((r) => r.status === "draft" || r.status === "approved");
  const earlier = rows.filter((r) => r.status === "superseded");
  const [showPrevious, setShowPrevious] = useState(false);
  const [phase, setPhase] = useState<Phase | null>(null);
  const [history, setHistory] = useState(false);
  const [recorded, setRecorded] = useState<ConfigApproveOutput | null>(null);

  // the bodies the table draws: the version in effect always, the earlier ones once *Show previous versions* is on
  const toOpen = [...(current ? [current] : []), ...(showPrevious ? earlier : [])];
  const opened = useQueries({
    queries: toOpen.map((row) => ({
      queryKey: ["config-open", row.id, ""],
      queryFn: () => configOpen({ id: row.id }),
      staleTime: 60_000,
      retry: false,
    })),
  });
  const bodyOf = new Map<string, Body | null | undefined>(toOpen.map((row, i) => [row.id, opened[i]?.data?.payload ?? undefined]));
  const currentBody = current ? bodyOf.get(current.id) : null;
  const openError = opened.find((q) => q.error)?.error as AppError | null | undefined;
  const loading = versions.isPending || (current !== undefined && currentBody === undefined && !openError);
  const tableRows = versionRows(
    kind,
    toOpen.map((row) => ({ version: row.version, status: row.status, body: bodyOf.get(row.id) })),
  );
  const editable = can("config.propose");
  const start = (focus?: string) => setPhase({ step: "change", body: cloneBody(currentBody ?? emptyBody(kind)), focus });

  return (
    <Stack gap={16}>
      <Refusal error={versions.error} onRetry={() => void versions.refetch()} />
      <Refusal error={openError} />
      {recorded ? (
        <Stack gap={8}>
          <JourneyStrip stage="done" subs={{ activate: VERSION_STATUS_WORD[recorded.status] }} />
          <Recorded live right={recorded.approved_at ? formatDateTime(recorded.approved_at, timeZone) : undefined}>
            {word} <span className="mc-mono">{VERSION_TEMPLATES.version({ n: recorded.version })}</span> · {VERSION_STATUS_WORD[recorded.status]}
          </Recorded>
        </Stack>
      ) : null}
      {waiting.map((row) => (
        <WaitingVersion
          key={row.id}
          row={row}
          kind={kind}
          word={word}
          current={current}
          currentBody={currentBody}
          timeZone={timeZone}
          onRecorded={setRecorded}
        />
      ))}
      <ConfigTable
        label={word}
        valueHead={listField(kind) === "labels" ? CONSOLE_LABELS.status : CONSOLE_LABELS.active}
        rows={tableRows}
        showPrevious={showPrevious}
        onShowPreviousChange={setShowPrevious}
        onEdit={(slug) => start(slug)}
        onViewHistory={() => setHistory(true)}
        onNewVersion={() => start()}
        mode={editable ? "edit" : "view"}
        loading={loading}
        
      />
      {phase?.step === "change" ? (
        <SlideOver
          title={word}
          onClose={() => setPhase(null)}
          dismissLabel={ACTION_LABELS.cancel}
          primary={{ label: ACTION_LABELS.validate, onClick: () => setPhase({ step: "journey", body: phase.body }), hs: "cfg.validate" }}
          
        >
          <Stack gap={14}>
            <JourneyStrip stage="change" />
            <ListEditor
              kind={kind}
              configKey={configKey}
              value={phase.body}
              onChange={(body) => setPhase({ ...phase, body })}
              published={currentBody ?? null}
              focus={phase.focus}
            />
          </Stack>
        </SlideOver>
      ) : null}
      {phase?.step === "journey" ? (
        <Journey
          kind={kind}
          configKey={configKey}
          body={phase.body}
          label={word}
          previous={currentBody ?? emptyBody(kind)}
          onBack={() => setPhase({ step: "change", body: phase.body })}
          onDone={() => setPhase(null)}
        />
      ) : null}
      {history ? (
        <SlideOver title={SURFACE_LABELS.viewHistory} onClose={() => setHistory(false)} >
          <Versions
            label={word}
            timeZone={timeZone}
            rows={rows
              .filter((r) => r.status === "effective" || r.status === "superseded")
              .map((r): VersionRow => ({
                n: r.version,
                at: r.approved_at ?? r.effective_from,
                actor: r.approved_by_name ?? r.published_by_name ?? r.published_by_id ?? "—",
                actorKey: r.approved_by_id ?? r.published_by_id ?? undefined,
                reason: r.note ?? "—",
              }))}
          />
        </SlideOver>
      ) : null}
    </Stack>
  );
}

// ---- a version waiting on its second Admin (Activate) or on its date ----------------------------------------------------

interface WaitingProps {
  row: ProposalRow;
  kind: string;
  word: string;
  current: ProposalRow | undefined;
  currentBody: Body | null | undefined;
  timeZone: string;
  onRecorded: (result: ConfigApproveOutput) => void;
}

function WaitingVersion({ row, kind, word, current, currentBody, timeZone, onRecorded }: WaitingProps) {
  const { can } = useSession();
  const opened = useRead<ConfigOpenOutput>(
    ["config-open", row.id, current?.id ?? ""],
    () => configOpen({ id: row.id, diff_against: current?.id ?? null }),
  );
  const approve = useWrite(configApprove, [["config"], ["config-open"]]);
  const reject = useWrite(configReject, [["config"], ["config-open"]]);
  const [rejecting, setRejecting] = useState(false);
  const [why, setWhy] = useState("");
  const version = VERSION_TEMPLATES.version({ n: row.version });
  const draft = row.status === "draft";
  const decides = draft && can("config.approve");
  const rows = opened.data?.payload
    ? changeRows(kind, currentBody === undefined ? undefined : (currentBody ?? emptyBody(kind)), opened.data.payload, opened.data.diff)
    : [];

  const doApprove = async () => {
    const result = await approve.mutateAsync({ proposal_id: row.id }).catch(() => null);
    if (result) onRecorded(result);
  };
  const doReject = async () => {
    const result = await reject.mutateAsync({ proposal_id: row.id, reason: why.trim() }).catch(() => null);
    if (result) setRejecting(false);
  };

  return (
    <Card
      title={<>{word} <span className="mc-mono">{version}</span></>}
      pills={<Pill tone={draft ? "warn" : "info"}>{VERSION_STATUS_WORD[row.status]}</Pill>}
      actions={decides ? (
        <Flex gap={8}>
          <Button tone="ghost" onClick={() => setRejecting(true)} hs={hsKey("cfg.reject", row.version)}>{CONSOLE_LABELS.reject}</Button>
          <Button tone="primary" busy={approve.isPending} onClick={() => void doApprove()} hs={hsKey("cfg.approve", row.version)}>
            {ACTION_LABELS.approve}
          </Button>
        </Flex>
      ) : undefined}
    >
      <Stack gap={12}>
        <JourneyStrip
          stage={draft ? "activate" : "audit"}
          subs={{ version, activate: draft ? PANEL_CHANGE_STATUS.draft : SCHEDULING_STATUS.scheduled }}
        />
        <Refusal error={approve.error} />
        <KeyValue
          items={[
            [HISTORY_LABELS.actor, row.published_by_name ?? row.published_by_id ?? "—"],
            [HISTORY_LABELS.timestamp, row.published_at ? formatDateTime(row.published_at, timeZone) : "—"],
            [JOURNEY_LABELS.activate, formatDateTime(row.effective_from, timeZone)],
            [HISTORY_LABELS.reason, row.note ?? "—", undefined, 3],
          ]}
        />
        <Refusal error={opened.error} onRetry={() => void opened.refetch()} />
        {rows.length > 0 ? <Diff form="cols" rows={rows} label={word} /> : null}
      </Stack>
      {rejecting ? (
        <SlideOver
          title={CONSOLE_LABELS.reject}
          onClose={() => setRejecting(false)}
          dismissLabel={ACTION_LABELS.cancel}
          primary={{
            label: CONSOLE_LABELS.reject,
            onClick: () => void doReject(),
            blocked: why.trim().length < 3 ? { reason: COMMON_LABELS.required } : undefined,
            busy: reject.isPending,
            hs: hsKey("cfg.reject.confirm", row.version),
          }}
          hs={hsKey("cfg.reject.cancel", row.version)}
        >
          <Stack gap={12}>
            <Refusal error={reject.error} />
            <TextArea label={HISTORY_LABELS.reason} value={why} onChange={(event) => setWhy(event.target.value)} rows={3} required  />
          </Stack>
        </SlideOver>
      ) : null}
    </Card>
  );
}
